import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Appointment, Bundle } from 'fhir/r4';

import { backendEndPointToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';
import { EventsService } from '../events/events.service';
import { LoadingUIEnabled, SkipHospitalSegregation } from '../loading.interceptor';
import { UnifiedActivity, mapAppointmentToActivity, mapEventToActivity } from './unified-activity.model';

@Injectable({ providedIn: 'root' })
export class UnifiedActivitiesService {
  private http = inject(HttpClient);
  private backendUrl = inject(backendEndPointToken);
  private authService = inject(AuthService);
  private eventsService = inject(EventsService);

  // Codes treated as "telemedicine" — mirrors the logic already used in
  // AppointmentComponent.resolveAppointmentServiceCategory / isTelemedicine.
  private readonly virtualHints = ['telemedicine', 'telemed', 'telehealth', 'virtual', 'tele'];

  getUpcomingActivities(): Observable<UnifiedActivity[]> {
    return forkJoin([
      this.fetchUpcomingAppointments(),
      this.fetchUpcomingEvents()
    ]).pipe(
      map(([appointments, events]) => {
        const merged = [...appointments, ...events];
        merged.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
        return merged;
      })
    );
  }

  private fetchUpcomingAppointments(): Observable<UnifiedActivity[]> {
    const actorRef = this.getLoggedInActorReference();
    if (!actorRef) {
      return of([]);
    }

    const nowIso = new Date().toISOString();
    const context = new HttpContext().set(LoadingUIEnabled, false).set(SkipHospitalSegregation, true);
    const statuses = 'proposed,booked,arrived,pending,waitlist';
    const url = `${this.backendUrl}/Appointment?actor=${encodeURIComponent(actorRef)}`
      + `&date=ge${nowIso}&status=${statuses}&_count=100&_sort=date`;

    return this.http.get<Bundle<Appointment>>(url, { context }).pipe(
      map(bundle => (bundle.entry ?? [])
        .map(entry => entry.resource as Appointment)
        .filter(Boolean)
        .map(appt => mapAppointmentToActivity(appt, this.isTelemedicine(appt)))
      ),
      catchError((err) => {
        console.error('[UnifiedActivitiesService] Failed to fetch appointments', err);
        return of([]);
      })
    );
  }

  private fetchUpcomingEvents(): Observable<UnifiedActivity[]> {
    return this.eventsService.getVisibleEvents().pipe(
      map(response => {
        const now = new Date();
        return (response.data ?? [])
          .filter(event => event.isActive !== false && new Date(event.end) >= now)
          .map(mapEventToActivity);
      }),
      catchError((err) => {
        console.error('[UnifiedActivitiesService] Failed to fetch events', err);
        return of([]);
      })
    );
  }

  private isTelemedicine(appt: Appointment): boolean {
    const haystack = [
      ...(appt.serviceCategory ?? []).flatMap(c => [c.text, ...(c.coding ?? []).map(x => x.display), ...(c.coding ?? []).map(x => x.code)]),
      appt.appointmentType?.text,
      ...(appt.appointmentType?.coding ?? []).map(c => c.display),
      ...(appt.appointmentType?.coding ?? []).map(c => c.code)
    ]
      .filter(Boolean)
      .map(v => String(v).toLowerCase());

    return haystack.some(v => this.virtualHints.some(hint => v.includes(hint)));
  }

  private getLoggedInActorReference(): string {
    const user = this.authService.user.getValue();
    if (!user) return '';

    const practitionerRoles = ['doctor', 'healthcareassistant', 'lab', 'pharmacy', 'remotedoctor', 'hospitaladmin', 'healthcareadmin', 'alliedhealthprofessional', 'cashier'];
    if (user.role && practitionerRoles.includes(user.role)) {
      return `Practitioner/${user['fhirResourceId']}`;
    }
    if (user.role === 'patient') {
      return `Patient/${user['fhirResourceId']}`;
    }
    return '';
  }
}
